import {html, nothing, PropertyValues, TemplateResult} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {classMap} from "lit/directives/class-map.js";
import {repeat} from "lit/directives/repeat.js";
import {when} from "lit/directives/when.js";
import {Issue} from "../../domain/entities/issue.entity";
import {StringHelper} from "../../helpers/string.helper";
import {AbstractLitElement} from "../abstract-lit.element";
import {TabItem} from "./tab-item.interface";
import {rootUidOf} from "../../domain/helpers/issue-type-root.helper";

@customElement(`ha-tab-list`)
export class HaTabListComponent extends AbstractLitElement {

    @property({type: Number, attribute: `index`})
    public activeItemIndex: number = 0;

    @property({type: Array})
    public items: TabItem<Issue>[] = [];

    @property({type: String})
    public orientation: 'vertical' | 'horizontal' = 'horizontal';

    private _activeItem: TabItem | null = null;

    public render(): TemplateResult {

        return html`
            <ul class="tab-list">
                ${repeat(this.items, item => item.id, (item, index) => {
                    const itemCssClasses = {
                        "tab-item": true,
                        "active": index === this.activeItemIndex,
                        // An issue without a status (statusless types after the data migration) gets no status class.
                        ...(item.payload.status ? {[`status-${StringHelper.toKebabCase(item.payload.status.key.toLowerCase())}`]: true} : {})
                    }

                    return html`
                        <li class=${classMap(itemCssClasses)}
                            data-root-type=${rootUidOf(item.payload.type) ?? nothing}
                            @click=${(evt: MouseEvent) => this._onClickTab(item, evt)}
                        >
                            ${when(item.prefix, () => html`
                                <span class="prefix"
                                      data-type="prefix"
                                >${item.prefix}</span>
                            `)}
                            <span class=${classMap({title: true, ...item.cssClasses ?? {}})}
                                  data-type="title"
                            >${item.title}</span>
                            ${when(!!item.postfix, () => html`
                                <span class="postfix"
                                      data-type="postfix"
                                >${item.postfix}</span>
                            `)}
                        </li>
                    `;
                })}
            </ul>
        `;
    }

    protected willUpdate(_changedProperties: PropertyValues) {
        if (_changedProperties.has(`items`) || _changedProperties.has(`activeItemIndex`)) {
            this._activeItem = this.items[this.activeItemIndex];
        }
        if (_changedProperties.has(`orientation`)) {
            this.classList.remove(`horizontal`, `vertical`);
            this.classList.add(this.orientation);
        }
    }

    private _onClickTab(item: TabItem, evt: MouseEvent): void {
        const type = ((evt.target as HTMLElement).dataset?.type ?? null) as 'title' | 'prefix' | 'postfix' | null;

        if (!type || !(type in item)) {
            return;
        }

        this._activeItem = item;
        this.activeItemIndex = this.items.findIndex(i => i.id === item.id);

        this.dispatchEvent(
            new CustomEvent(`ha-tab.click`, {detail: {value: item}})
        );
        this.dispatchEvent(
            new CustomEvent(`ha-tab.${type}.click`, {detail: {value: item}})
        );
    }

}